/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import * as React from 'react';

import { Dialog } from '@base-ui/react/dialog';
import { Drawer, DrawerCloseState, DrawerTriggerState } from '@base-ui/react/drawer';

import { useBreakpoint } from 'sarsaparilla';
import { ComponentRenderFn, HTMLProps } from '@base-ui/react/types';

/**
 * Which Base UI primitive `ResponsiveDialog` is currently rendering.
 * - `'drawer'` on small viewports (`isSmAndDown`).
 * - `'dialog'` otherwise (including during SSR, when all `useBreakpoint`
 *   flags resolve to `false`).
 */
export type ResponsiveDialogMode = 'drawer' | 'dialog';

interface ResponsiveDialogContextValue {
    mode: ResponsiveDialogMode;
}

const ResponsiveDialogContext = React.createContext<ResponsiveDialogContextValue | null>(
    null
);

/**
 * Throws when a compound part is rendered outside `<ResponsiveDialog.Root>`,
 * which would otherwise produce a confusing Base UI error.
 */
function useResponsiveDialogContext(part: string): ResponsiveDialogContextValue {
    const ctx = React.useContext(ResponsiveDialogContext);
    if (ctx === null) {
        throw new Error(
            `<ResponsiveDialog.${part}> must be rendered inside <ResponsiveDialog.Root>.`
        );
    }
    return ctx;
}

/**
 * Returns the active `ResponsiveDialog` mode so consumers can branch in JSX
 * without subscribing to `useBreakpoint` themselves (ensuring both follow the
 * same `isSmAndDown` threshold).
 */
export function useResponsiveDialogMode(): ResponsiveDialogMode {
    return useResponsiveDialogContext('Root').mode;
}

interface ResponsiveDialogRootProps {
    /** Controlled open state. Forwarded to the active Base UI Root. */
    open?: boolean;
    /** Initial open state when uncontrolled. */
    defaultOpen?: boolean;
    /**
     * Open-change handler. The eventDetails arg from Base UI is omitted from
     * the public signature to keep the API uniform across modes.
     */
    onOpenChange?: (open: boolean) => void;
    /** Mirrors Base UI's `modal` prop; supports `'trap-focus'`. */
    modal?: boolean | 'trap-focus';
    /** Whether outside clicks dismiss the popup. */
    disablePointerDismissal?: boolean;
    /** Trigger ID for controlled mode. */
    triggerId?: string | null;
    /** Trigger ID for initially-open uncontrolled mode. */
    defaultTriggerId?: string | null;
    /** Compound parts of the responsive dialog. */
    children?: React.ReactNode;
}

/**
 * Groups all parts of a `ResponsiveDialog`. Selects `Drawer.Root` on small
 * viewports and `Dialog.Root` otherwise, and exposes the active mode through
 * context so descendant parts can mirror the choice.
 *
 * @remarks
 * The mode is recomputed on every render via `useBreakpoint`, so a viewport
 * resize that crosses the `sm` threshold will swap the underlying Base UI
 * primitive. In uncontrolled mode this resets the open state — pass `open` /
 * `onOpenChange` if you need to persist the open state across a resize.
 */
function Root({ children, ...rest }: ResponsiveDialogRootProps): React.ReactElement {
    const { isSmAndDown } = useBreakpoint();
    const mode: ResponsiveDialogMode = isSmAndDown ? 'drawer' : 'dialog';

    const value = React.useMemo<ResponsiveDialogContextValue>(() => ({ mode }), [mode]);

    return (
        <ResponsiveDialogContext.Provider value={value}>
            {mode === 'drawer' ? (
                <Drawer.Root {...rest}>{children}</Drawer.Root>
            ) : (
                <Dialog.Root {...rest}>{children}</Dialog.Root>
            )}
        </ResponsiveDialogContext.Provider>
    );
}

interface ResponsiveDialogTriggerProps extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'className'
> {
    className?: string;
    children?: React.ReactNode;
    render?:
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | ComponentRenderFn<HTMLProps, DrawerTriggerState>
        | undefined;
}

function Trigger({
    className,
    children,
    ...rest
}: ResponsiveDialogTriggerProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Trigger');
    const Part = mode === 'drawer' ? Drawer.Trigger : Dialog.Trigger;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

interface ResponsiveDialogPortalProps {
    /** Renders the popup tree, including the portal target. */
    children?: React.ReactNode;
    /**
     * Whether to keep the popup mounted in the DOM while closed. Forwarded to
     * the underlying Base UI Portal.
     */
    keepMounted?: boolean;
    /**
     * The container to portal into. Forwarded to the underlying Base UI Portal.
     */
    container?: React.RefObject<HTMLElement | null> | HTMLElement | null;
    className?: string;
}

function Portal({ children, ...rest }: ResponsiveDialogPortalProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Portal');
    const Part = mode === 'drawer' ? Drawer.Portal : Dialog.Portal;
    return <Part {...rest}>{children}</Part>;
}

interface ResponsiveDialogDivProps extends Omit<
    React.HTMLAttributes<HTMLDivElement>,
    'className'
> {
    className?: string;
    children?: React.ReactNode;
}

function Backdrop({
    className,
    children,
    ...rest
}: ResponsiveDialogDivProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Backdrop');
    const Part = mode === 'drawer' ? Drawer.Backdrop : Dialog.Backdrop;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

function Viewport({
    className,
    children,
    ...rest
}: ResponsiveDialogDivProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Viewport');
    const Part = mode === 'drawer' ? Drawer.Viewport : Dialog.Viewport;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

interface ResponsiveDialogPopupProps extends ResponsiveDialogDivProps {
    /**
     * The element to focus when the popup closes. Forwarded to the underlying
     * Base UI Popup.
     */
    finalFocus?: Dialog.Popup.Props['finalFocus'];
}

function Popup({
    className,
    children,
    ...rest
}: ResponsiveDialogPopupProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Popup');
    const Part = mode === 'drawer' ? Drawer.Popup : Dialog.Popup;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

interface ResponsiveDialogTitleProps extends Omit<
    React.HTMLAttributes<HTMLHeadingElement>,
    'className'
> {
    className?: string;
    children?: React.ReactNode;
}

function Title({
    className,
    children,
    ...rest
}: ResponsiveDialogTitleProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Title');
    const Part = mode === 'drawer' ? Drawer.Title : Dialog.Title;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

interface ResponsiveDialogDescriptionProps extends Omit<
    React.HTMLAttributes<HTMLParagraphElement>,
    'className'
> {
    className?: string;
    children?: React.ReactNode;
}

function Description({
    className,
    children,
    ...rest
}: ResponsiveDialogDescriptionProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Description');
    const Part = mode === 'drawer' ? Drawer.Description : Dialog.Description;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

interface ResponsiveDialogCloseProps extends Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'className'
> {
    className?: string;
    children?: React.ReactNode;
    render?:
        | React.ReactElement<any, string | React.JSXElementConstructor<any>>
        | ComponentRenderFn<HTMLProps, DrawerCloseState>
        | undefined;
}

function Close({
    className,
    children,
    ...rest
}: ResponsiveDialogCloseProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Close');
    const Part = mode === 'drawer' ? Drawer.Close : Dialog.Close;
    return (
        <Part className={className} data-mode={mode} {...rest}>
            {children}
        </Part>
    );
}

/**
 * Inner content wrapper. On `'drawer'` mode renders `<Drawer.Content>` (Base
 * UI provides this layout slot for the scrollable region inside the popup).
 * On `'dialog'` mode renders a plain `<div>` with the same `className`, so a
 * single `.Content` style rule can target both.
 */
function Content({
    className,
    children,
    ...rest
}: ResponsiveDialogDivProps): React.ReactElement {
    const { mode } = useResponsiveDialogContext('Content');
    if (mode === 'drawer') {
        return (
            <Drawer.Content className={className} data-mode={mode} {...rest}>
                {children}
            </Drawer.Content>
        );
    }
    return (
        <div className={className} data-mode={mode} {...rest}>
            {children}
        </div>
    );
}

/**
 * Drag handle rendered only in `'drawer'` mode. In `'dialog'` mode this
 * returns `null`, so consumers can declare it unconditionally and have it
 * disappear on desktop.
 */
function Handle({
    className,
    ...rest
}: ResponsiveDialogDivProps): React.ReactElement | null {
    const { mode } = useResponsiveDialogContext('Handle');
    if (mode !== 'drawer') {
        return null;
    }
    return <div className={className} data-mode={mode} {...rest} />;
}

/**
 * `ResponsiveDialog` is a Base UI-style compound component that renders as a
 * `Drawer` on `isSmAndDown` viewports and a `Dialog` otherwise. Consumers
 * declare a single tree of `ResponsiveDialog.*` parts, and each part picks the
 * appropriate underlying primitive.
 *
 * @example
 * ```tsx
 * <ResponsiveDialog.Root>
 *     <ResponsiveDialog.Trigger className={styles.Button}>Open</ResponsiveDialog.Trigger>
 *     <ResponsiveDialog.Portal>
 *         <ResponsiveDialog.Backdrop className={styles.Backdrop} />
 *         <ResponsiveDialog.Viewport className={styles.Viewport}>
 *             <ResponsiveDialog.Popup className={styles.Popup}>
 *                 <ResponsiveDialog.Handle className={styles.Handle} />
 *                 <ResponsiveDialog.Content className={styles.Content}>
 *                     <ResponsiveDialog.Title>Title</ResponsiveDialog.Title>
 *                     <ResponsiveDialog.Close>Close</ResponsiveDialog.Close>
 *                 </ResponsiveDialog.Content>
 *             </ResponsiveDialog.Popup>
 *         </ResponsiveDialog.Viewport>
 *     </ResponsiveDialog.Portal>
 * </ResponsiveDialog.Root>
 * ```
 */
export const ResponsiveDialog = {
    Root,
    Trigger,
    Portal,
    Backdrop,
    Viewport,
    Popup,
    Title,
    Description,
    Close,
    Content,
    Handle,
} as const;
